import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Package, 
  Bell, Trophy, Gift, HelpCircle, Shield, Settings,
  Menu, X, LogOut, ChevronDown, UserCircle, Ticket,
  FileText, MessageSquare, Star, ClipboardList, ShoppingBag,
  Sun, Moon, Building2, Image, Receipt, Monitor, Tablet, Code,
  ScrollText
} from 'lucide-react';

const menuItems = [
  { 
    label: 'Dashboard', 
    path: '/admin/dashboard', 
    icon: LayoutDashboard,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  { 
    label: 'Venue', 
    path: '/admin/venue', 
    icon: Building2,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Users', 
    path: '/admin/users', 
    icon: Users,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Courts', 
    path: '/admin/courts', 
    icon: ClipboardList,
    roles: ['super_admin', 'manager', 'supervisor']
  },
  { 
    label: 'Bookings', 
    path: '/admin/bookings', 
    icon: Calendar,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  { 
    label: 'Special Packages', 
    path: '/admin/special-packages', 
    icon: Package,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Point of Sales', 
    path: '/admin/pos', 
    icon: CreditCard,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  {
    label: 'Transactions',
    path: '/admin/transactions',
    icon: Receipt,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  {
    label: 'Web Log',
    path: '/admin/web-log',
    icon: ScrollText,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  { 
    label: 'Add-ons', 
    path: '/admin/addons', 
    icon: ShoppingBag,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Reports', 
    path: '/admin/payments', 
    icon: FileText,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Open Play', 
    path: '/admin/open-play', 
    icon: Users,
    roles: ['super_admin', 'manager', 'supervisor']
  },
  { 
    label: 'Events', 
    path: '/admin/events', 
    icon: Trophy,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Gallery', 
    path: '/admin/gallery', 
    icon: Image,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Memberships', 
    path: '/admin/memberships', 
    icon: Ticket,
    roles: ['super_admin']
  },
  { 
    label: 'Reviews', 
    path: '/admin/reviews', 
    icon: Star,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Rewards', 
    path: '/admin/rewards', 
    icon: Gift,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Notifications', 
    path: '/admin/notifications', 
    icon: Bell,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Support', 
    path: '/admin/support', 
    icon: MessageSquare,
    roles: ['super_admin', 'manager', 'supervisor']
  },
  { 
    label: 'FAQs', 
    path: '/admin/faqs', 
    icon: HelpCircle,
    roles: ['super_admin', 'manager']
  },
  { 
    label: 'Admin Users', 
    path: '/admin/admins', 
    icon: Shield,
    roles: ['super_admin']
  },
  { 
    label: 'Logs', 
    path: '/admin/logs', 
    icon: ScrollText,
    roles: ['super_admin'],
    emailRestricted: 'admin@camsurpickleball.com' // Only this specific email can see this
  },
  { 
    label: 'Kiosk POS', 
    path: '/admin/pos-bookings', 
    icon: Monitor,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  { 
    label: 'Booking Kiosk', 
    path: '/admin/kiosk-bookings', 
    icon: Tablet,
    roles: ['super_admin', 'manager', 'supervisor', 'cashier']
  },
  { 
    label: 'Embed Posts', 
    path: '/admin/embed', 
    icon: Code,
    roles: ['super_admin', 'manager', 'content_manager']
  }
];

const roleLabels = {
  super_admin: 'Super Admin',
  manager: 'Manager',
  supervisor: 'Supervisor',
  cashier: 'Cashier',
  content_manager: 'Content Manager'
};

const AdminLayout = ({ children }) => {
  const { admin, logout, hasRole } = useAdminAuth();
  const { isDarkMode, toggleTheme, theme } = useAdminTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter menu items based on role AND email restriction
  const filteredMenu = menuItems.filter(item => {
    // First check role
    if (!hasRole(item.roles)) return false;
    
    // If there's an email restriction, check it
    if (item.emailRestricted && admin?.email !== item.emailRestricted) {
      return false;
    }
    
    return true;
  });

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 ${theme.card} border-r
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-emerald-400" />
            </div>
            <span className={`font-semibold ${theme.text}`}>Admin Panel</span>
          </Link>
          <button 
            className={`lg:hidden ${theme.textMuted}`}
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {filteredMenu.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${isActive 
                    ? theme.badgeSuccess
                    : `${theme.textMuted} ${isDarkMode ? 'hover:text-white hover:bg-slate-700/50' : 'hover:text-gray-900 hover:bg-gray-100'}`
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${theme.card}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <UserCircle className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${theme.text} truncate`}>{admin?.name}</p>
              <p className={`text-xs ${theme.textSubtle}`}>{roleLabels[admin?.role]}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className={`h-16 ${theme.card} backdrop-blur-sm border-b sticky top-0 z-30`}>
          <div className="h-full px-4 flex items-center justify-between">
            <button 
              className={`lg:hidden ${theme.textMuted}`}
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className={`${theme.textMuted} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                data-testid="admin-theme-toggle"
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className={`${theme.textMuted} ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
